<template>
  <div class="instance-manage">
    <div class="instance-hero">
      <div class="instance-hero__icon">
        <v-icon>settings_applications</v-icon>
      </div>
      <div class="instance-hero__content">
        <div class="instance-hero__title">Instance Management</div>
        <div class="instance-hero__sub">
          {{ company.company_name || "School" }} setup and server payload details
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-chip
        small
        label
        :color="hasInstanceSetup ? 'green lighten-5' : 'orange lighten-5'"
        :text-color="hasInstanceSetup ? 'green darken-2' : 'orange darken-3'"
        class="instance-status"
      >
        <v-icon left small>{{ hasInstanceSetup ? 'check_circle' : 'warning' }}</v-icon>
        {{ hasInstanceSetup ? 'Instance Ready' : 'Setup Required' }}
      </v-chip>
    </div>

    <div class="instance-metrics">
      <div class="metric-card">
        <div class="metric-card__icon metric-card__icon--blue">
          <v-icon>vpn_key</v-icon>
        </div>
        <div>
          <div class="metric-card__label">Server ID</div>
          <div class="metric-card__value">{{ company.company_no || "-" }}</div>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-card__icon metric-card__icon--teal">
          <v-icon>settings_ethernet</v-icon>
        </div>
        <div>
          <div class="metric-card__label">Port</div>
          <div class="metric-card__value">{{ company.port || "-" }}</div>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-card__icon metric-card__icon--slate">
          <v-icon>public</v-icon>
        </div>
        <div>
          <div class="metric-card__label">Domain</div>
          <div class="metric-card__value">{{ company.domain || "-" }}</div>
        </div>
      </div>
    </div>

    <div class="payload-panel server-config-panel">
      <div class="payload-panel__header">
        <div>
          <div class="payload-panel__title">Server Configuration</div>
          <div class="payload-panel__sub">Editable routing values for this school</div>
        </div>
        <v-icon class="payload-panel__icon">dns</v-icon>
      </div>
      <v-layout row wrap>
        <v-flex xs12 sm5 class="pr-sm-3 mb-3">
          <form-text-field
            v-model="company.url"
            label="Server URL"
            required
            :rules="[rules.required]"
            prepend-inner-icon="link"
            placeholder="http://localhost"
          ></form-text-field>
        </v-flex>
        <v-flex xs12 sm3 class="pr-sm-3 mb-3">
          <form-text-field
            v-model="company.port"
            label="Port"
            required
            :rules="[rules.required]"
            type="number"
            prepend-inner-icon="settings_ethernet"
            placeholder="3002"
          ></form-text-field>
        </v-flex>
        <v-flex xs12 sm4 class="mb-3">
          <form-text-field
            v-model="company.company_no"
            label="Server Identifier"
            disabled
            prepend-inner-icon="vpn_key"
            readonly-style
          ></form-text-field>
        </v-flex>
      </v-layout>
    </div>

    <v-layout row wrap>
      <v-flex xs12 md4 class="pr-md-3 mb-3">
        <div class="info-panel">
          <div class="info-panel__label">Company Type</div>
          <div class="info-panel__value">
            {{ company.company_type === 1 ? "School" : "Institute" }}
          </div>
          <div class="info-panel__meta">
            {{ company.company_sub_type === 1 ? "Primary" : "Secondary" }}
          </div>
        </div>
      </v-flex>

      <v-flex xs12 md8 class="mb-3">
        <div class="info-panel">
          <div class="info-panel__label">Modules</div>
          <div class="module-list" v-if="companyModules.length">
            <span class="module-chip" v-for="module in companyModules" :key="module">
              <v-icon x-small>extension</v-icon>
              {{ formatModuleName(module) }}
            </span>
          </div>
          <div class="empty-inline" v-else>No modules assigned</div>
        </div>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 md6 class="pr-md-3 mb-3">
        <div class="payload-panel">
          <div class="payload-panel__header">
            <div>
              <div class="payload-panel__title">Company Payload</div>
              <div class="payload-panel__sub">Account-facing fields</div>
            </div>
            <v-icon class="payload-panel__icon">business</v-icon>
          </div>
          <div class="payload-grid">
            <div class="payload-row" v-for="[key, value] in Object.entries(companyInfo)" :key="key">
              <div class="payload-row__key">{{ formatLabel(key) }}</div>
              <div class="payload-row__value">{{ formatValue(value) }}</div>
            </div>
          </div>
        </div>
      </v-flex>

      <v-flex xs12 md6 class="mb-3">
        <div class="payload-panel">
          <div class="payload-panel__header">
            <div>
              <div class="payload-panel__title">Server Payload</div>
              <div class="payload-panel__sub">Provisioning and routing details</div>
            </div>
            <v-icon class="payload-panel__icon">dns</v-icon>
          </div>
          <div class="payload-grid">
            <div class="payload-row" v-for="[key, value] in Object.entries(serverPayload)" :key="key">
              <div class="payload-row__key">{{ formatLabel(key) }}</div>
              <div class="payload-row__value">{{ formatValue(value) }}</div>
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>

    <div class="instance-actions">
      <v-btn flat color="secondary" class="instance-action-btn" @click="listRegisteredSchools">
        <v-icon left small>refresh</v-icon>
        Refresh
      </v-btn>
      <v-btn
        depressed
        color="primary"
        class="instance-action-btn instance-action-btn--primary"
        :loading="submitting"
        @click="createCompany"
      >
        <v-icon left small>save</v-icon>
        Save Changes
      </v-btn>
      <v-btn
        v-if="!hasInstanceSetup"
        depressed
        color="primary"
        class="instance-action-btn instance-action-btn--primary"
        @click="openCreateUserDialog"
      >
        <v-icon left small>person_add</v-icon>
        Create User
      </v-btn>
    </div>

    <v-dialog v-model="createUserDialog" max-width="460px">
      <v-card class="manage-dialog">
        <div class="modal-header">
          <div class="modal-header__left">
            <div class="modal-header__icon-wrap">
              <v-icon class="modal-header__icon">person_add</v-icon>
            </div>
            <div>
              <div class="modal-header__title">Create school user?</div>
              <div class="modal-header__sub">Provision the instance account</div>
            </div>
          </div>
          <v-btn icon flat class="modal-close-btn" :disabled="creatingUser" @click="createUserDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </div>

        <v-divider class="modal-divider"></v-divider>

        <div class="modal-body">
          <p class="dialog-copy">
            This will create the login user for {{ company.company_name || "this school" }} and mark the instance as ready once the server responds.
          </p>
        </div>

        <v-divider class="modal-divider"></v-divider>

        <div class="modal-footer">
          <v-btn flat class="modal-cancel-btn" :disabled="creatingUser" @click="createUserDialog = false">
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="primary"
            class="modal-action-btn"
            :loading="creatingUser"
            @click="createCompanyUserAccount"
          >
            <v-icon left small>person_add</v-icon>
            Create User
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import APICompany from "@/mixins/APICompany";
import FormTextField from "@/components/form/FormTextField";

export default {
  components: {
    FormTextField
  },
  mixins: [APICompany],
  props: {
    company: {
      type: Object,
      default: function() {
        return {
          company_no: "edpass_",
          resource_limit: 100,
          password: ""
        };
      }
    }
  },
  data() {
    return {
      draft: false,
      email: "",
      port: "",
      schools: [],
      createUserDialog: false,
      creatingUser: false,
      submitting: false,
      rules: {
        required: value => !!value || "Required."
      }
    };
  },
  watch: {
    company: {
      handler: function() {
        this.savePendingSchool();

        if (this.port && !this.company.port) this.company.port = this.port;
        if (!this.company.timezone) this.company.timezone = "Australia/Sydney";
        if (!this.company.url) this.company.url = "http://localhost";
        if (!this.company.domain && this.company.port) {
          this.company.domain = "https://portal.edpass.co/" + this.company.port;
        }
        this.syncServerIdentifierPort();
      },
      deep: true
    }
  },
  computed: {
    hasInstanceSetup() {
      if (!this.company) return false;
      return !!(this.company.company_id && this.company.company_id.length === 24);
    },
    companyInfo() {
      let info = {};
      if (!this.company) return info;

      for (const key of Object.keys(this.company)) {
        if (
          ![
            "resource_limit",
            "status",
            "lat",
            "long",
            "_id",
            "company_name",
            "email",
            "password",
            "temp_pw",
            "created_date",
            "updated_date"
          ].includes(key)
        ) {
          continue;
        }
        info[key] = this.company[key];
      }
      return info;
    },
    serverPayload() {
      let info = {};
      if (!this.company) return info;

      for (const key of Object.keys(this.company)) {
        if (!["_id", "port", "url", "timezone", "domain", "company_no", "company_id"].includes(key)) {
          continue;
        }
        info[key] = this.company[key];
      }
      return info;
    },
    companyModules() {
      return this.company && this.company.modules ? this.company.modules : [];
    }
  },
  mounted() {
    this.resumePendingSchool();
    this.listRegisteredSchools();
  },
  methods: {
    normalizeDomain: function(value) {
      const domain = String(value || "").trim();
      if (!domain) return domain;
      if (/^https?:\/\//i.test(domain)) return domain;
      return `https://${domain}`;
    },
    buildCompanyPayload: function() {
      const fields = [
        "_id",
        "company_id",
        "company_no",
        "company_name",
        "company_type",
        "company_sub_type",
        "email",
        "mobile",
        "resource_limit",
        "status",
        "lat",
        "long",
        "url",
        "port",
        "timezone",
        "domain",
        "modules"
      ];
      let payload = {};

      fields.forEach(key => {
        if (this.company[key] !== undefined) payload[key] = this.company[key];
      });

      payload.domain = this.normalizeDomain(payload.domain);
      payload.port = payload.port !== undefined ? String(payload.port).trim() : payload.port;
      payload.url = payload.url !== undefined ? String(payload.url).trim() : payload.url;
      return payload;
    },
    createIdentifierBase: function() {
      const schoolName = String(this.company.company_name || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      if (!this.company._id && schoolName) {
        return ["edpass", schoolName].join("_");
      }

      const current = String(this.company.company_no || "").trim();
      const currentBase = current.replace(/_\d+$/, "").replace(/_+$/, "");
      if (currentBase && currentBase !== "edpass") return currentBase;

      return schoolName ? ["edpass", schoolName].join("_") : "edpass";
    },
    syncServerIdentifierPort: function() {
      if (!this.company) return;
      const port = String(this.company.port || "").trim();
      if (!port) return;

      const nextIdentifier = [this.createIdentifierBase(), port].join("_");
      if (this.company.company_no !== nextIdentifier) {
        this.company.company_no = nextIdentifier;
      }
    },
    openCreateUserDialog: function() {
      this.createUserDialog = true;
    },
    createCompanyUserAccount: async function() {
      try {
        this.creatingUser = true;
        if (this.company._id) {
          await this.registerCompanyUser(this.company._id);
          this.createUserDialog = false;
          this.$toast.success("User created successfully.", "Success", {
            position: "topCenter"
          });
        }
      } catch (error) {
        this.$toast.error("Failed to create user account", "Error", {
          position: "topCenter"
        });
      } finally {
        this.creatingUser = false;
      }
    },
    listRegisteredSchools: async function() {
      try {
        this.schools = await this.getCompanies();
        let maxPort = this.schools.map(obj => parseInt(obj.port)).sort().pop();
        if (maxPort) this.port = String(maxPort + 1);
      } catch (error) {
        // Keep the current form state if the schools list is unavailable.
      }
    },
    savePendingSchool: function() {
      if (this.company._id) return;
      sessionStorage.newCompany = JSON.stringify(this.company);
      this.draft = true;
    },
    removePendingSchool: function() {
      sessionStorage.removeItem("newCompany");
      this.$router.push({ name: "Company" });
    },
    resumePendingSchool: function() {
      if (sessionStorage.newCompany) {
        Object.assign(this.company, JSON.parse(sessionStorage.newCompany) || {});
        this.draft = true;
      } else {
        this.draft = false;
      }
    },
    resetPassword: async function() {
      try {
        if (this.company._id) {
          await this.putCompany({ _id: this.company._id, password: "123456" });
          this.$toast.success('Password reset successfully. Use "123456" as a new password.', "Success", {
            position: "topCenter"
          });
        }
      } catch (error) {
        this.$toast.error("Failed to submit details", "Error", {
          position: "topCenter"
        });
      }
    },
    handleDelete: async function() {
      try {
        await this.removeCompany();
        this.$router.push({ name: "Company" });
      } catch (error) {
        // removeCompany handles navigation fallback when deletion fails.
      }
    },
    removeCompany: async function() {
      try {
        if (this.company._id) await this.deleteCompany(this.company._id);
      } catch (error) {
        this.$router.push({ name: "Company" });
      }
    },
    createCompany: async function() {
      this.submitting = true;
      try {
        this.syncServerIdentifierPort();
        if (this.company._id) {
          await this.putCompany(this.buildCompanyPayload());
        } else {
          let ob = this.buildCompanyPayload();
          ob.password = this.company.password;
          ob.company_id = ob.company_no;
          let response = await this.postCompany(ob);
          this.$store.commit("setStudentSwitchState", {
            action: null,
            payload: response
          });
        }
        sessionStorage.removeItem("newCompany");
        this.$toast.success("Success", "submitted", { position: "topCenter" });
      } catch (error) {
        const message = error && error.response && error.response.data && (error.response.data.message || error.response.data.error)
          ? error.response.data.message || error.response.data.error
          : "Failed to submit details";
        this.$toast.error(message, "Error", {
          position: "topCenter"
        });
      } finally {
        this.submitting = false;
      }
    },
    formatLabel: function(key) {
      return `${key.substring(0, 1).toUpperCase()}${key.slice(1).replace(/_/g, " ")}`;
    },
    formatModuleName: function(module) {
      return String(module).replace(/_/g, " ").toUpperCase();
    },
    formatValue: function(value) {
      if (value === null || value === undefined || value === "") return "-";
      if (typeof value === "object") return JSON.stringify(value);
      return value;
    }
  }
};
</script>

<style scoped lang="css">
.instance-manage {
  padding: 0;
  background: #ffffff;
}

.instance-hero {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.instance-hero__content {
  min-width: 0;
}

.instance-hero__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1565C0, #0d47a1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.instance-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 16px;
}

.metric-card {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  min-width: 0;
  padding: 16px;
}

.metric-card__icon {
  align-items: center;
  border-radius: 8px;
  display: flex;
  flex-shrink: 0;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.metric-card__icon >>> .v-icon {
  color: #ffffff !important;
  font-size: 19px !important;
}

.metric-card__icon--blue {
  background: #1565C0;
}

.metric-card__icon--teal {
  background: #00838F;
}

.metric-card__icon--slate {
  background: #475569;
}

.metric-card__label {
  color: #64748b;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.3;
  text-transform: uppercase;
}

.metric-card__value {
  color: #0f172a;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  margin-top: 3px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.instance-hero__icon >>> .v-icon {
  color: #ffffff !important;
}

.instance-hero__title,
.payload-panel__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  color: #1e293b;
}

.instance-hero__sub,
.payload-panel__sub,
.info-panel__label {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  line-height: 1.45;
  color: #94a3b8;
}

.instance-status {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-weight: 600 !important;
}

.info-panel,
.payload-panel {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 16px;
  height: 100%;
}

.info-panel__label {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1.3;
  margin-bottom: 8px;
}

.info-panel__value {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #1e293b;
}

.info-panel__meta {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: #475569;
  margin-top: 2px;
}

.module-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.module-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(21, 101, 192, 0.08);
  color: #1565C0;
  padding: 6px 10px;
  border-radius: 8px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.3;
  min-height: 28px;
}

.module-chip >>> .v-icon {
  color: #1565C0 !important;
}

.empty-inline {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: #94a3b8;
}

.payload-panel {
  background: #ffffff;
}

.server-config-panel {
  margin-bottom: 16px;
}

.payload-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.payload-panel__icon {
  color: #94a3b8 !important;
}

.payload-grid {
  display: grid;
  gap: 0;
}

.payload-row {
  display: grid;
  grid-template-columns: minmax(130px, 0.8fr) minmax(0, 1.2fr);
  gap: 10px;
  min-height: 40px;
  padding: 10px 0;
  border-top: 1px solid #f1f5f9;
}

.payload-row__key {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  color: #64748b;
}

.payload-row__value {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: #1e293b;
  min-width: 0;
  overflow-wrap: anywhere;
}

.instance-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 10px;
}

.instance-action-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-weight: 500 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  height: 40px !important;
}

.instance-action-btn--primary {
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(21, 101, 192, 0.3) !important;
}

.manage-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
}

.modal-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-header__icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1565C0, #0d47a1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-header__icon {
  color: #ffffff !important;
  font-size: 20px !important;
}

.modal-header__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.modal-header__sub {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

.modal-close-btn {
  color: #94a3b8 !important;
}

.modal-divider {
  border-color: #f1f5f9 !important;
}

.modal-body {
  padding: 20px;
}

.dialog-copy {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
  margin: 0;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
}

.modal-cancel-btn,
.modal-action-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  text-transform: none !important;
  border-radius: 10px !important;
}

.modal-cancel-btn {
  color: #64748b !important;
}

.modal-action-btn {
  font-weight: 600 !important;
  height: 40px !important;
  padding: 0 18px !important;
}

@media (max-width: 600px) {
  .instance-manage {
    padding: 12px;
  }

  .instance-hero,
  .instance-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .instance-status {
    align-self: flex-start;
  }

  .instance-metrics {
    grid-template-columns: 1fr;
  }

  .payload-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
